import React from 'react';
import { formatPhoneDisplay, digitsOnly } from '@/utils/phoneFormat';

type BaseInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'>;

export interface PhoneInputProps extends BaseInputProps {
  /** Stored value (digits or any phone string — it is formatted for display). */
  value: string | null | undefined;
  /**
   * Called with the NEW value on each keystroke. By default this is the
   * digits-only string (recommended — keeps storage normalized for dedup/search).
   * Set emit="formatted" to receive the "(XXX) XXX-XXXX" display string instead.
   */
  onChange: (value: string) => void;
  emit?: 'digits' | 'formatted';
}

/**
 * Drop-in replacement for `<input type="tel">` that live-formats US/NANP numbers
 * as the user types and emits a normalized value via onChange(value: string).
 * All other input props (className, placeholder, disabled, id, name, required…)
 * are forwarded unchanged.
 */
export default function PhoneInput({ value, onChange, emit = 'digits', ...rest }: PhoneInputProps) {
  return (
    <input
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={formatPhoneDisplay(value)}
      onChange={(e) => {
        const next = e.target.value;
        onChange(emit === 'formatted' ? formatPhoneDisplay(next) : digitsOnly(next));
      }}
      {...rest}
    />
  );
}
